import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { Loader2, MapPin, Clock, Calendar, CheckCircle2, ExternalLink, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INGENIUM_LOGO = "https://customer-assets.emergentagent.com/job_agentic-curation/artifacts/zri9wsj7_ingenium-logo.png";

const AnniversaryRSVP = () => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    job_title: "",
    company: "",
    mobile: "",
  });
  const [followedLinkedIn, setFollowedLinkedIn] = useState(false);
  const [likedPost, setLikedPost] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [onInviteList, setOnInviteList] = useState(false);
  const [rsvpOpen, setRsvpOpen] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/rsvp-status`)
      .then(res => setRsvpOpen(res.data.rsvp_open))
      .catch(() => setRsvpOpen(false));
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.job_title || !form.company || !form.mobile) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!followedLinkedIn) {
      toast.error("Please follow our LinkedIn page to continue");
      return;
    }
    if (!likedPost) {
      toast.error("Please like our LinkedIn post to continue");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/rsvp`, form);
      setSubmitted(true);
      setOnInviteList(res.data.on_invite_list || false);
      toast.success(res.data.message || "RSVP submitted!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please fill in your name and email to decline");
      return;
    }
    setDeclining(true);
    try {
      const res = await axios.post(`${API}/rsvp/decline`, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
      });
      setDeclined(true);
      toast.success(res.data.message || "Decline recorded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setDeclining(false);
    }
  };

  if (declined) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-6">
        <div className="max-w-lg w-full text-center space-y-8">
          <img src={INGENIUM_LOGO} alt="Ingenium Digital" className="h-10 mx-auto" />
          <div>
            <h1 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              We'll Miss You, {form.first_name}!
            </h1>
            <p className="text-[#ebebeb]/60 text-base leading-relaxed">
              Thank you for letting us know. We hope to see you at a future event!
            </p>
          </div>
          <p className="text-[#ebebeb]/30 text-xs">
            Changed your mind?{" "}
            <button onClick={() => { setDeclined(false); }} className="text-[#ff3737]/70 hover:text-[#ff3737] underline">
              Go back to RSVP
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-6">
        <div className="max-w-lg w-full text-center space-y-8">
          <img src={INGENIUM_LOGO} alt="Ingenium Digital" className="h-10 mx-auto" />
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#ff3737] to-[#9b59b6] flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-10 h-10 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {onInviteList ? "You're Confirmed!" : `Thank You, ${form.first_name}!`}
            </h1>
            <p className="text-[#ebebeb]/60 text-base leading-relaxed">
              {onInviteList
                ? "Your RSVP is confirmed. We look forward to celebrating with you! A confirmation email will be sent shortly."
                : "Your RSVP has been received. We'll notify you if your attendance is confirmed. We look forward to celebrating with you!"}
            </p>
          </div>
          <div className="bg-[#1a1a1a] border border-white/5 rounded-2xl p-6 text-left space-y-3">
            <div className="flex items-center gap-3 text-[#ebebeb]/70">
              <Calendar className="w-4 h-4 text-[#ff3737] shrink-0" />
              <span className="text-sm">17th April 2026 (Friday)</span>
            </div>
            <div className="flex items-center gap-3 text-[#ebebeb]/70">
              <Clock className="w-4 h-4 text-[#ff3737] shrink-0" />
              <span className="text-sm">7:00 PM - 11:00 PM</span>
            </div>
            <div className="flex items-center gap-3 text-[#ebebeb]/70">
              <MapPin className="w-4 h-4 text-[#ff3737] shrink-0" />
              <span className="text-sm">Soho House Blue Room, 33 Des Voeux Road West, Sheung Wan, HK</span>
            </div>
          </div>
          <p className="text-[#ebebeb]/30 text-xs">
            For enquiries, contact{" "}
            <a href="mailto:alexis@ingenium-digital.com" className="text-[#ff3737]/70 hover:text-[#ff3737]">
              alexis@ingenium-digital.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] relative overflow-hidden">
      {/* RSVP Closed Overlay */}
      {rsvpOpen === false && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" data-testid="rsvp-closed-overlay">
          <div className="max-w-md w-full text-center space-y-6 bg-[#1a1a1a] border border-white/10 rounded-2xl p-8">
            <img src={INGENIUM_LOGO} alt="Ingenium Digital" className="h-8 mx-auto opacity-60" />
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                RSVP Closed
              </h2>
              <p className="text-[#ebebeb]/50 text-sm leading-relaxed">
                The RSVP deadline for our 4th Anniversary Party has passed. Thank you for your interest!
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-left space-y-2">
              <div className="flex items-center gap-3 text-[#ebebeb]/60">
                <Calendar className="w-4 h-4 text-[#ff3737] shrink-0" />
                <span className="text-sm">17th April 2026 (Friday)</span>
              </div>
              <div className="flex items-center gap-3 text-[#ebebeb]/60">
                <MapPin className="w-4 h-4 text-[#ff3737] shrink-0" />
                <span className="text-sm">Soho House Blue Room, Sheung Wan, HK</span>
              </div>
            </div>
            <p className="text-[#ebebeb]/30 text-xs">
              Questions? Contact{" "}
              <a href="mailto:alexis@ingenium-digital.com" className="text-[#ff3737]/70 hover:text-[#ff3737]">
                alexis@ingenium-digital.com
              </a>
            </p>
          </div>
        </div>
      )}
      {/* Colorful gradient background matching homepage */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `
          radial-gradient(at 1% 81%, rgba(249, 248, 83, 0.18) 0px, transparent 50%),
          radial-gradient(at 20% 49%, rgba(156, 113, 7, 0.14) 0px, transparent 50%),
          radial-gradient(at 87% 86%, rgba(153, 6, 89, 0.18) 0px, transparent 50%),
          radial-gradient(at 79% 56%, rgba(245, 91, 153, 0.18) 0px, transparent 50%),
          radial-gradient(at 50% 79%, rgba(225, 157, 55, 0.2) 0px, transparent 50%)
        `,
        backgroundBlendMode: 'screen',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <img src={INGENIUM_LOGO} alt="Ingenium Digital" className="h-8 sm:h-10 mx-auto mb-8" />
          <p className="text-[#ff3737]/80 text-xs sm:text-sm tracking-[0.3em] uppercase mb-4 font-medium">You're Invited</p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="rsvp-title"
          >
            4th Anniversary Celebration
          </h1>
          <p className="text-[#ebebeb]/50 max-w-xl mx-auto text-sm sm:text-base leading-relaxed">
            We're thrilled to invite you to celebrate this special milestone with us.
            For the past four years, your support has been the fuel that drives us.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left Column — Event Info + LinkedIn */}
          <div className="space-y-6 order-2 lg:order-1">
            {/* Event Card */}
            <div className="bg-[#141414] border border-white/5 rounded-2xl p-6 sm:p-8 space-y-5">
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                Event Details
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#ff3737]/10 flex items-center justify-center shrink-0">
                    <Calendar className="w-5 h-5 text-[#ff3737]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">17th April 2026</p>
                    <p className="text-[#ebebeb]/40 text-xs">Friday</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#ff3737]/10 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-[#ff3737]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">7:00 PM - 11:00 PM</p>
                    <p className="text-[#ebebeb]/40 text-xs">Drinks, Snacks & Celebration</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#ff3737]/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-[#ff3737]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Soho House Blue Room</p>
                    <p className="text-[#ebebeb]/40 text-xs">33 Des Voeux Road West, Sheung Wan, Hong Kong</p>
                  </div>
                </div>
              </div>
              <div className="border-t border-white/5 pt-4 space-y-2">
                <p className="text-[#ebebeb]/30 text-xs leading-relaxed">
                  Please RSVP before <span className="text-[#ff3737]/70">April 15th, 2026</span>.
                </p>
                <p className="text-[#ebebeb]/30 text-xs leading-relaxed">
                  Invite is non-transferable. RSVP does not guarantee entry. You will be notified if your RSVP is confirmed. Entry may be limited if venue capacity is reached.
                </p>
              </div>
            </div>

            {/* LinkedIn Section */}
            <div className="bg-[#141414] border border-white/5 rounded-2xl p-6 sm:p-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Connect With Us
                </h2>
                <a
                  href="https://www.linkedin.com/company/ingenium-digital-hong-kong-limited/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-[#0a66c2] hover:text-[#0a66c2]/80 transition-colors"
                  data-testid="linkedin-follow-link"
                >
                  Follow on LinkedIn <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="rounded-xl overflow-hidden bg-white mb-4" style={{ maxHeight: "400px", overflowY: "auto" }}>
                <iframe
                  src="https://www.linkedin.com/embed/feed/update/urn:li:ugcPost:7444291703806521345"
                  height="400"
                  width="100%"
                  frameBorder="0"
                  allowFullScreen
                  title="Ingenium Digital LinkedIn Post"
                  className="w-full"
                  data-testid="linkedin-embed"
                />
              </div>
            </div>
          </div>

          {/* Right Column — RSVP Form */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-8">
            <div className="bg-[#141414] border border-white/5 rounded-2xl p-6 sm:p-8">
              <h2
                className="text-lg font-semibold text-white mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                RSVP
              </h2>
              <p className="text-[#ebebeb]/40 text-xs mb-6">Reserve your spot at our celebration</p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="rsvp-form">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[#ebebeb]/70 text-xs">First Name *</Label>
                    <Input
                      type="text"
                      placeholder="First name"
                      value={form.first_name}
                      onChange={(e) => handleChange("first_name", e.target.value)}
                      className="bg-[#1a1a1a] border-white/5 text-white placeholder:text-white/20 h-10 text-sm focus:border-[#ff3737]/30 focus:ring-0"
                      required
                      data-testid="rsvp-first-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#ebebeb]/70 text-xs">Last Name *</Label>
                    <Input
                      type="text"
                      placeholder="Last name"
                      value={form.last_name}
                      onChange={(e) => handleChange("last_name", e.target.value)}
                      className="bg-[#1a1a1a] border-white/5 text-white placeholder:text-white/20 h-10 text-sm focus:border-[#ff3737]/30 focus:ring-0"
                      required
                      data-testid="rsvp-last-name"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#ebebeb]/70 text-xs">Email *</Label>
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    className="bg-[#1a1a1a] border-white/5 text-white placeholder:text-white/20 h-10 text-sm focus:border-[#ff3737]/30 focus:ring-0"
                    required
                    data-testid="rsvp-email"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#ebebeb]/70 text-xs">Job Title *</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Media Director"
                    value={form.job_title}
                    onChange={(e) => handleChange("job_title", e.target.value)}
                    className="bg-[#1a1a1a] border-white/5 text-white placeholder:text-white/20 h-10 text-sm focus:border-[#ff3737]/30 focus:ring-0"
                    required
                    data-testid="rsvp-job-title"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#ebebeb]/70 text-xs">Company *</Label>
                  <Input
                    type="text"
                    placeholder="Company name"
                    value={form.company}
                    onChange={(e) => handleChange("company", e.target.value)}
                    className="bg-[#1a1a1a] border-white/5 text-white placeholder:text-white/20 h-10 text-sm focus:border-[#ff3737]/30 focus:ring-0"
                    required
                    data-testid="rsvp-company"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#ebebeb]/70 text-xs">Mobile Number *</Label>
                  <Input
                    type="tel"
                    placeholder="+852 XXXX XXXX"
                    value={form.mobile}
                    onChange={(e) => handleChange("mobile", e.target.value)}
                    className="bg-[#1a1a1a] border-white/5 text-white placeholder:text-white/20 h-10 text-sm focus:border-[#ff3737]/30 focus:ring-0"
                    required
                    data-testid="rsvp-mobile"
                  />
                </div>

                {/* LinkedIn Checkboxes */}
                <div className="border-t border-white/5 pt-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="follow-linkedin"
                      checked={followedLinkedIn}
                      onCheckedChange={setFollowedLinkedIn}
                      className="mt-0.5 border-white/20 data-[state=checked]:bg-[#ff3737] data-[state=checked]:border-[#ff3737]"
                      data-testid="rsvp-linkedin-follow-checkbox"
                    />
                    <label htmlFor="follow-linkedin" className="text-[#ebebeb]/60 text-xs leading-relaxed cursor-pointer">
                      I have followed{" "}
                      <a
                        href="https://www.linkedin.com/company/ingenium-digital-hong-kong-limited/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#0a66c2] hover:underline"
                      >
                        Ingenium Digital on LinkedIn
                      </a>
                    </label>
                  </div>
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="like-post"
                      checked={likedPost}
                      onCheckedChange={setLikedPost}
                      className="mt-0.5 border-white/20 data-[state=checked]:bg-[#ff3737] data-[state=checked]:border-[#ff3737]"
                      data-testid="rsvp-linkedin-like-checkbox"
                    />
                    <label htmlFor="like-post" className="text-[#ebebeb]/60 text-xs leading-relaxed cursor-pointer">
                      I have liked the{" "}
                      <a
                        href="https://www.linkedin.com/feed/update/urn:li:activity:7444565384793333760/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#0a66c2] hover:underline"
                      >
                        post on LinkedIn
                      </a>
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || declining || rsvpOpen === false}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-[#ff3737] to-[#9b59b6] text-white font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="rsvp-submit-btn"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Submitting...
                    </span>
                  ) : (
                    "Confirm RSVP"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleDecline}
                  disabled={declining || submitting || rsvpOpen === false}
                  className="w-full py-2.5 rounded-xl border border-white/10 text-[#ebebeb]/50 hover:text-white hover:border-white/25 text-sm transition-all disabled:opacity-50"
                  data-testid="rsvp-decline-btn"
                >
                  {declining ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Processing...
                    </span>
                  ) : (
                    "Unable to attend"
                  )}
                </button>
              </form>

              <p className="text-[#ebebeb]/20 text-[10px] mt-4 text-center">
                For enquiries, contact{" "}
                <a href="mailto:alexis@ingenium-digital.com" className="hover:text-[#ebebeb]/40">
                  alexis@ingenium-digital.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnniversaryRSVP;
